import { useEffect, useState } from 'react'
import { OutputPresetId } from '@/content/instagram'
import { useEditSession } from '@/core/store/editSession'
import { PreviewTab } from '@/core/store/uiState'
import { TEST_IDS } from '@/lib/testIds'
import { useIsCoarsePointer } from '@/lib/usePointerType'
import { cn } from '@/lib/utils'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/ui/shadcn/dialog'
import { invalidatePreviewCache } from '@/tools/preview/renderPreviewTiles'
import { renderPreviewTiles, type PreviewTile } from '@/tools/preview/renderPreviewTiles'
import { ProfilePreview } from '@/tools/preview/ProfilePreview'
import { PostPreview } from '@/tools/preview/PostPreview'
import { StoryPreview } from '@/tools/preview/StoryPreview'

const TAB_LABELS: Readonly<Record<PreviewTab, string>> = {
  [PreviewTab.Profile]: 'Profile',
  [PreviewTab.Post]: 'Post',
}

export function PreviewOverlay() {
  const document = useEditSession((state) => state.document)
  const previewOpen = useEditSession((state) => state.previewOpen)
  const previewTab = useEditSession((state) => state.previewTab)
  const sources = useEditSession((state) => state.sources)
  const exportOptions = useEditSession((state) => state.exportOptions)
  const setPreviewOpen = useEditSession((state) => state.setPreviewOpen)
  const setPreviewTab = useEditSession((state) => state.setPreviewTab)
  const coarsePointer = useIsCoarsePointer()
  const [rendered, setRendered] = useState<{
    document: NonNullable<typeof document>
    sources: typeof sources
    exportOptions: typeof exportOptions
    tiles: readonly PreviewTile[]
  } | null>(null)
  const [renderError, setRenderError] = useState<Error | null>(null)
  useEffect(() => useEditSession.subscribe(invalidatePreviewCache), [])
  useEffect(() => {
    if (!previewOpen || !document) return
    let cancelled = false
    void renderPreviewTiles(document, sources, exportOptions).then((tiles) => {
      if (!cancelled) setRendered({ document, sources, exportOptions, tiles })
    }).catch((error: unknown) => {
      if (!cancelled) setRenderError(error instanceof Error ? error : new Error('Instagram preview rendering failed'))
    })
    return () => {
      cancelled = true
    }
  }, [document, exportOptions, previewOpen, sources])
  if (!document) {
    if (previewOpen) throw new Error('Instagram preview cannot open without a document')
    return null
  }
  const story = document.output.presetId === OutputPresetId.Story
  const tiles = rendered?.document === document
    && rendered.sources === sources
    && rendered.exportOptions === exportOptions
    ? rendered.tiles
    : null
  if (renderError) throw renderError

  return (
    <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
      <DialogContent
        data-testid={TEST_IDS.previewOverlay}
        className={cn(
          'flex h-[min(90dvh,780px)] max-w-[430px] flex-col gap-0 overflow-hidden p-0',
          coarsePointer && 'h-dvh max-h-none w-screen max-w-none rounded-none border-0',
        )}
      >
        <DialogHeader className="border-b px-5 py-4 text-left">
          <DialogTitle>Instagram preview</DialogTitle>
          <DialogDescription>{story
            ? 'See the current export behind the interface areas of an Instagram story.'
            : 'See how the current export fits in a profile or feed post.'}</DialogDescription>
        </DialogHeader>
        <div className="border-b p-2" role="tablist" aria-label="Instagram preview view">
          {story ? (
            <div className="rounded-md bg-muted px-3 py-2 text-center text-sm font-medium">Story</div>
          ) : Object.values(PreviewTab).map((tab) => (
            <button
              key={tab}
              type="button"
              role="tab"
              aria-selected={previewTab === tab}
              data-testid={TEST_IDS.previewTab(tab)}
              onClick={() => setPreviewTab(tab)}
              className="w-1/2 rounded-md px-3 py-2 text-sm font-medium text-muted-foreground aria-selected:bg-muted aria-selected:text-foreground"
            >
              {TAB_LABELS[tab]}
            </button>
          ))}
        </div>
        {!tiles ? (
          <div className="grid min-h-0 flex-1 place-items-center bg-muted/30 p-6 text-center text-sm text-muted-foreground" data-testid={TEST_IDS.previewLoading}>Rendering preview…</div>
        ) : story ? (
          <StoryPreview tiles={tiles} />
        ) : previewTab === PreviewTab.Profile ? (
          <ProfilePreview tiles={tiles} split={document.output.split} />
        ) : (
          <PostPreview tiles={tiles} split={document.output.split} />
        )}
      </DialogContent>
    </Dialog>
  )
}
